import React, { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
    HiOutlineHome,
    HiOutlineBriefcase,
    HiOutlineChatAlt2,
    HiOutlineCog,
    HiOutlineBell
} from "react-icons/hi";

// Shared Mock Data
import { mockBrandCampaigns } from "./BrandProfile";

const CreatorCampaignDetail = () => {
    const { campaignId } = useParams();
    const [hasApplied, setHasApplied] = useState(false); // Toggle between Apply and Applied

    // Mock data for Adidas brand
    const brandData = {
        name: "Adidas",
        logo: "fa-brands fa-adidas text-3xl"
    };

    // Find the campaign from mock data or use a fallback
    const campaign = mockBrandCampaigns.find(c => c.id === campaignId) || mockBrandCampaigns[0];

    return (
        <div className="flex h-screen bg-[#FDFBF7]">
            <Sidebar />

            <main className="flex-1 overflow-hidden flex flex-col h-screen p-4">
                {/* Top Header */}
                <header className="flex justify-between items-center mb-6 shrink-0 h-12">
                    <div className="flex-1 max-w-2xl bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm mx-4 invisible">
                        {/* Hidden search bar just to keep layout spacing accurate if needed */}
                    </div>
                    
                    <div className="flex items-center gap-6 ml-4">
                        <button className="relative text-gray-500 hover:text-gray-700">
                            <HiOutlineBell className="text-2xl" />
                        </button>
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-gray-800 hidden md:block">Johan Doe</span>
                            <div className="w-10 h-10 rounded-full bg-[#0044CC] flex items-center justify-center text-white font-bold shadow-md overflow-hidden">
                                J
                            </div>
                        </div>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto pb-6 custom-scrollbar pr-2">
                    <div className="max-w-6xl mx-auto space-y-6">
                        
                        {/* Title Section */}
                        <div className="mb-2">
                            <div className="flex items-center gap-4 mb-1">
                                <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center font-bold text-lg shadow-sm">
                                    {brandData.name.charAt(0)}
                                </div>
                                <h1 className="text-2xl font-black tracking-tight text-gray-900">{brandData.name}</h1>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900 tracking-tight">{campaign.title}</h2>
                        </div>

                        {/* Content Container */}
                        <div className="bg-white rounded-[2rem] p-6 lg:p-8 border border-gray-100 shadow-sm flex flex-col lg:flex-row gap-6 lg:gap-8 flex-1 min-h-0">
                            
                            {/* Left: Image Box */}
                            <div className="lg:w-1/3 shrink-0 flex flex-col items-center">
                                <div className={`w-full aspect-[4/5] max-h-[280px] object-cover ${campaign.cardColor} rounded-[1.5rem] mb-4`}></div>
                                
                                {/* Pill Tags under image */}
                                <div className="flex flex-wrap gap-1.5 mt-2 px-1 w-full">
                                    {campaign.tags.map((tag, idx) => (
                                        <span 
                                            key={idx} 
                                            className={`px-3 py-1.5 text-[11px] font-bold rounded-full ${tag.bg} ${tag.text}`}
                                        >
                                            {tag.label}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Right: Details & Apply */}
                            <div className="lg:w-2/3 flex flex-col h-full">
                                <h3 className="text-md font-bold text-gray-900 mb-2">Description</h3>
                                <p className="text-sm text-gray-600 leading-relaxed mb-6 pr-8">
                                    Create a quick, 60-second reel that highlights the product's key features and benefits. Use dynamic transitions and engaging visuals to demonstrate how the product works. Incorporate a compelling call-to-action to encourage viewers to learn more or make a purchase. Aim for a high-energy, polished presentation that grabs attention and maintains viewer interest.
                                </p>

                                <h3 className="text-md font-bold text-gray-900 mb-2">Requirements</h3>
                                
                                <div className="space-y-2 mb-6">
                                    <RequirementRow label="Niche" value=": Fashion & Beauty, Tech & Gadgets, or Food & Beverage." />
                                    <RequirementRow label="Gender" value=": Female" />
                                    <RequirementRow label="Audience Size" value=": 8,000 to 10,000 followers." />
                                    <RequirementRow label="Content Style" value=": Must be proficient in video content creation." />
                                    <RequirementRow label="Location" value=": Open to creators in India" />
                                    <RequirementRow label="Compensation" value=": Paid collaboration" underline />
                                </div>

                                {/* Apply Button Section */}
                                <div className="mt-auto pt-4 flex justify-center lg:justify-end lg:pr-8">
                                    {!hasApplied ? (
                                        <button 
                                            onClick={() => setHasApplied(true)}
                                            className="bg-[#0044CC] text-white font-bold text-sm px-12 py-3 rounded-full hover:bg-blue-700 transition-all shadow-md hover:shadow-lg active:scale-95"
                                        >
                                            Apply
                                        </button>
                                    ) : (
                                        <button 
                                            onClick={() => setHasApplied(false)} // For testing purposes to toggle it back
                                            className="bg-[#EEEDFC] text-[#0044CC] font-bold text-sm px-12 py-3 rounded-full transition-all active:scale-95"
                                        >
                                            Applied
                                        </button>
                                    )}
                                </div>
                            </div>

                        </div>

                    </div>
                </div>

            </main>
        </div>
    );
};

const RequirementRow = ({ label, value, underline }) => (
    <div className="flex text-sm text-gray-600">
        <span className="w-32 font-bold text-gray-900 shrink-0">{label}</span>
        <span className={`text-gray-500 ${underline ? 'underline decoration-gray-300 underline-offset-4' : ''}`}>
            {value}
        </span>
    </div>
);

// Extracted Sidebar to reuse identically
const Sidebar = () => {
    const location = useLocation();
    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    return (
        <aside className="w-64 bg-white border-r border-gray-100 flex flex-col hidden md:flex shrink-0 z-20">
            <div className="p-6 flex items-center gap-2 text-black font-bold text-xl">
                <img src="/logo.png" alt="Braccet" className="w-8 h-8 object-contain" />
                <span>Braccet</span>
            </div>

            <nav className="flex-1 px-4 py-6 space-y-2">
                <SidebarItem icon={<HiOutlineHome />} label="Dashboard" to="/creator/dashboard" active={location.pathname === '/creator/dashboard'} />
                <SidebarItem icon={<HiOutlineBriefcase />} label="Campaigns" to="/creator/find-work" />
                <SidebarItem icon={<span className="font-bold text-lg">(B)</span>} label="Brands" to="/creator/brands" active={isActive('/creator/brands')} />
                <SidebarItem icon={<HiOutlineChatAlt2 />} label="Messages" to="/creator/messages" />
                <SidebarItem icon={<HiOutlineCog />} label="Settings" to="/creator/profile" />
            </nav>

            <div className="p-4 border-t border-gray-100">
                <button className="flex w-full items-center gap-3 p-2 rounded-xl text-gray-500 font-bold hover:bg-gray-50 hover:text-red-500 transition-colors">
                    <svg className="w-6 h-6 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    Logout
                </button>
            </div>
        </aside>
    );
};

const SidebarItem = ({ icon, label, to, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${active ? 'bg-blue-50 text-[#0044CC]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
        <span className="text-xl">{icon}</span>
        {label}
    </Link>
);

export default CreatorCampaignDetail;
